import React, { useState, useEffect, useCallback } from 'react';
import {
  Avatar, Button, Card, Col, Divider, Modal, Row, Select,
  Statistic, Table, Tag, Tooltip, Typography, message, Space,
} from 'antd';
import {
  CheckCircleOutlined, CopyOutlined, CrownOutlined, GiftOutlined,
  ShareAltOutlined, UserOutlined, WalletOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { getReferralsApi, getWalletApi, redeemRewardsApi, getPlansApi } from '../../api/rewards';
import { USER_IMAGE } from '../../utils/constants';
import { formatDate, formatCurrency } from '../../utils/formatters';

const { Title, Text, Paragraph } = Typography;

const PLAN_BADGE = {
  kh_1: 'default',
  kh_2: 'blue',
  kh_3: 'purple',
  kh_4: 'gold',
};

const SORT_OPTIONS = [
  { value: 'referred_at',          label: 'Referred On' },
  { value: 'referrer_commission',  label: 'Commission' },
  { value: 'subscription_id',      label: 'Plan' },
];

const FILTER_OPTIONS = [
  { value: '',  label: 'All' },
  { value: '1', label: 'Subscribed' },
  { value: '0', label: 'Not Subscribed' },
];

export default function Refer() {
  const { user } = useSelector((s) => s.auth);
  const userId   = user?.id;

  const [promoter, setPromoter]   = useState(null);
  const [settings, setSettings]   = useState(null);
  const [summary, setSummary]     = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [refMeta, setRefMeta]     = useState({ total: 0, current_page: 1, per_page: 20 });
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [subscribedFilter, setSubscribedFilter] = useState('');
  const [sortBy, setSortBy]       = useState('referred_at');

  const [wallet, setWallet]             = useState(null);
  const [walletSummary, setWalletSummary] = useState(null);
  const [walletLogs, setWalletLogs]     = useState([]);
  const [loadingWallet, setLoadingWallet] = useState(false);

  const [plans, setPlans]   = useState([]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [redeeming, setRedeeming]     = useState(false);
  const [redeemResult, setRedeemResult] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);

  const fetchReferrals = useCallback(async (page = 1) => {
    if (!userId) return;
    setLoadingReferrals(true);
    try {
      const params = { user_id: userId, sort_by: sortBy, sort_dir: 'desc', per_page: refMeta.per_page, page };
      if (subscribedFilter !== '') params.subscribed = subscribedFilter;
      const res  = await getReferralsApi(params);
      const body = res.data;
      setPromoter(body.promoter || null);
      setSettings(body.settings || null);
      setSummary(body.summary || null);
      setReferrals(body.data || []);
      setRefMeta(body.meta || refMeta);
    } catch {
      message.error('Failed to load referrals');
    } finally {
      setLoadingReferrals(false);
    }
  }, [userId, sortBy, subscribedFilter, refMeta.per_page]);

  const fetchWallet = useCallback(async () => {
    if (!userId) return;
    setLoadingWallet(true);
    try {
      const res  = await getWalletApi({ user_id: userId });
      const body = res.data;
      setWallet(body.wallet_balance ?? null);
      setWalletSummary(body.summary || null);
      setWalletLogs(body.logs || []);
    } catch {
      message.error('Failed to load wallet');
    } finally {
      setLoadingWallet(false);
    }
  }, [userId]);

  useEffect(() => {
    Promise.all([fetchReferrals(1), fetchWallet()]);
    getPlansApi().then((res) => setPlans(res.data?.data || res.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchReferrals(1); }, [sortBy, subscribedFilter]);

  const eligibleReferrals = referrals.filter((r) => r.is_subscribed === 1 && r.is_used_as_subscription === 0);
  const eligibleCount     = eligibleReferrals.length;
  const eligibleAmount    = eligibleReferrals.reduce((s, r) => s + (r.referrer_commission || 0), 0);
  const hasRedeemable     = eligibleCount > 0;

  const referralCode = promoter?.share_ref_code || user?.share_ref_code || `KHETI${userId}`;
  const referralLink = `https://khetihisab.com/signup?ref=${referralCode}`;
  const percentage   = settings?.referrer_percentage ?? '—';

  const copyCode = () => navigator.clipboard.writeText(referralCode).then(() => message.success('Referral code copied!'));
  const copyLink = () => navigator.clipboard.writeText(referralLink).then(() => message.success('Referral link copied!'));

  const handleRedeem = async () => {
    setRedeeming(true);
    try {
      const res  = await redeemRewardsApi({ user_id: userId });
      setRedeemResult(res.data);
      setConfirmOpen(false);
      setSuccessOpen(true);
      await Promise.all([fetchReferrals(1), fetchWallet()]);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Redeem failed');
    } finally {
      setRedeeming(false);
    }
  };

  // ── Wallet logs grouped by redeemed_at date ──
  const logGroups = walletLogs.reduce((acc, log) => {
    const date = formatDate(log.redeemed_at);
    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {});

  // ── Referral table columns ──
  const referralColumns = [
    {
      title: 'User',
      key: 'user',
      render: (_, r) => (
        <Space>
          <Avatar
            src={r.user_photo ? USER_IMAGE(r.user_photo) : null}
            icon={<UserOutlined />}
            size={32}
            style={{ background: '#e8f5e9', color: '#1a6b2a', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.user_name || '-'}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Mobile',
      key: 'mobile',
      width: 130,
      render: (_, r) => (
        <Text style={{ fontSize: 12 }}>{r.country_code} {r.user_mobile || '-'}</Text>
      ),
    },
    {
      title: 'Plan',
      key: 'plan',
      width: 110,
      render: (_, r) => r.plan_name
        ? <Tag color={PLAN_BADGE[r.subscription_id] || 'default'}>{r.plan_name}</Tag>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Subscribed',
      key: 'subscribed',
      width: 120,
      render: (_, r) => (
        <Space size={4}>
          {r.is_subscribed === 1
            ? <Tag color="green" icon={<CrownOutlined />}>Yes</Tag>
            : <Tag color="default">No</Tag>}
          {r.is_subscribed === 1 && r.is_used_as_subscription === 0 && <Tag color="orange">Pending</Tag>}
          {r.is_subscribed === 1 && r.is_used_as_subscription === 1 && <Tag color="default">Redeemed</Tag>}
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'subscription_amount',
      width: 100,
      render: (v) => v != null ? <Text style={{ fontSize: 12 }}>{formatCurrency(v)}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Commission',
      dataIndex: 'referrer_commission',
      width: 110,
      render: (v, r) => r.subscription_amount == null
        ? <Text type="secondary">—</Text>
        : <Text style={{ fontWeight: 600, color: '#1a6b2a' }}>{formatCurrency(v || 0)}</Text>,
    },
    {
      title: 'Referred On',
      dataIndex: 'referred_at',
      width: 120,
      render: (v) => v ? <Text style={{ fontSize: 12 }}>{formatDate(v)}</Text> : '-',
    },
  ];

  return (
    <div>
      <Title level={4} style={{ color: '#1a6b2a', marginBottom: 20 }}>
        <GiftOutlined /> Rewards & Earn
      </Title>

      {/* ── Info Banner ── */}
      <Card
        style={{
          borderRadius: 12, marginBottom: 20,
          background: 'linear-gradient(135deg, #1a6b2a, #52c41a)',
          border: 'none',
        }}
      >
        <Row gutter={[24, 16]} align="top">
          <Col xs={24} md={14}>
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>Referral Commission</Text>
            <Title level={4} style={{ color: '#fff', margin: '4px 0 8px' }}>
              Earn {percentage}% when your referral subscribes
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
              Share your code — when your friend subscribes, you earn automatically.
              {plans.length > 0 && ' Earnings per plan:'}
            </Text>

            {plans.length > 0 && (
              <div style={{ marginTop: 12, background: 'rgba(255,255,255,0.12)', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'rgba(0,0,0,0.15)' }}>
                      {['Plan', 'Price', 'You Earn'].map((h) => (
                        <th key={h} style={{ padding: '6px 12px', color: 'rgba(255,255,255,0.7)', fontWeight: 500, textAlign: 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((p) => (
                      <tr key={p.id} style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                        <td style={{ padding: '6px 12px', color: '#fff', fontWeight: 500 }}>{p.name}</td>
                        <td style={{ padding: '6px 12px', color: 'rgba(255,255,255,0.85)' }}>{formatCurrency(p.price)}</td>
                        <td style={{ padding: '6px 12px', color: '#fff', fontWeight: 700 }}>
                          {settings?.referrer_percentage
                            ? formatCurrency(p.price * settings.referrer_percentage / 100)
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {plans.length > 0 && (
                  <div style={{ padding: '6px 12px', fontSize: 11, color: 'rgba(255,255,255,0.6)', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    Share your code and earn when friends subscribe to any plan.
                  </div>
                )}
              </div>
            )}
          </Col>

          <Col xs={24} md={10} style={{ textAlign: 'center' }}>
            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '12px 20px', marginBottom: 12, display: 'inline-block' }}>
              <Text style={{ fontSize: 28, fontWeight: 800, letterSpacing: 4, color: '#fff' }}>{referralCode}</Text>
            </div>
            <div>
              <Space>
                <Button size="small" icon={<CopyOutlined />} onClick={copyCode}
                  style={{ background: '#fff', borderColor: '#fff', color: '#1a6b2a', fontWeight: 600 }}>
                  Copy Code
                </Button>
                <Button size="small" icon={<ShareAltOutlined />} onClick={copyLink} ghost>
                  Copy Link
                </Button>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      {/* ── 4 Summary Cards ── */}
      {summary && (
        <Row gutter={16} style={{ marginBottom: 20 }}>
          {[
            { label: 'Total Referred',    value: summary.total_referred,          color: '#1a6b2a', bg: '#f6ffed' },
            { label: 'Subscribed',        value: summary.total_subscribed,        color: '#d4a017', bg: '#fffbe6' },
            { label: 'Total Earned',      value: summary.total_commission_earned, color: '#1677ff', bg: '#e6f4ff', prefix: '₹' },
            { label: 'Available Balance', value: summary.wallet_balance,          color: '#ff4d4f', bg: '#fff1f0', prefix: '₹' },
          ].map((item) => (
            <Col xs={12} sm={6} key={item.label}>
              <Card style={{ borderRadius: 8, background: item.bg, border: 'none' }}>
                <Statistic
                  title={item.label}
                  value={item.value ?? 0}
                  prefix={item.prefix}
                  valueStyle={{ color: item.color, fontSize: 20, fontWeight: 'bold' }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* ── Wallet Card ── */}
      <Card style={{ borderRadius: 12, marginBottom: 20, textAlign: 'center' }}>
        <WalletOutlined style={{ fontSize: 28, color: '#1a6b2a', marginBottom: 4 }} />
        <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 4 }}>Wallet Balance</div>
        <div style={{ fontSize: 40, fontWeight: 800, color: '#1a6b2a', marginBottom: 16 }}>
          {formatCurrency(wallet ?? 0)}
        </div>

        <Row gutter={16} style={{ marginBottom: 20 }}>
          {[
            { label: 'Total Earned',   value: walletSummary?.total_earned ?? 0,   color: '#1677ff', bg: '#e6f4ff' },
            { label: 'Pending Payout', value: walletSummary?.pending_payout ?? 0, color: '#ff4d4f', bg: '#fff1f0' },
            { label: 'Total Paid',     value: walletSummary?.total_withdrawn ?? 0, color: '#52c41a', bg: '#f6ffed' },
          ].map((item) => (
            <Col xs={8} key={item.label}>
              <Card style={{ borderRadius: 8, background: item.bg, border: 'none' }}>
                <Statistic
                  title={item.label}
                  value={item.value}
                  prefix="₹"
                  valueStyle={{ color: item.color, fontSize: 16, fontWeight: 'bold' }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        <Tooltip title={!hasRedeemable ? 'No new referrals to redeem' : ''}>
          <Button
            type="primary"
            size="large"
            icon={<WalletOutlined />}
            disabled={!hasRedeemable}
            onClick={() => setConfirmOpen(true)}
            style={{ background: hasRedeemable ? '#1a6b2a' : undefined, borderColor: hasRedeemable ? '#1a6b2a' : undefined }}
          >
            Redeem to Wallet
          </Button>
        </Tooltip>
        {hasRedeemable && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#9ca3af' }}>
            {eligibleCount} referral{eligibleCount !== 1 ? 's' : ''} eligible · est. {formatCurrency(eligibleAmount)}
          </div>
        )}
      </Card>

      {/* ── Referred Users Table ── */}
      <Card
        style={{ borderRadius: 12, marginBottom: 20 }}
        title={
          <Space>
            <UserOutlined />
            <span>Referred Users</span>
            {summary && <Text type="secondary" style={{ fontSize: 13 }}>({summary.total_referred})</Text>}
          </Space>
        }
        extra={
          <Space>
            <Select
              value={subscribedFilter}
              onChange={setSubscribedFilter}
              options={FILTER_OPTIONS}
              size="small"
              style={{ width: 140 }}
            />
            <Select
              value={sortBy}
              onChange={setSortBy}
              options={SORT_OPTIONS}
              size="small"
              style={{ width: 140 }}
            />
          </Space>
        }
      >
        <Table
          rowKey="id"
          columns={referralColumns}
          dataSource={referrals}
          loading={loadingReferrals}
          size="small"
          scroll={{ x: 800 }}
          pagination={{
            current: refMeta.current_page,
            pageSize: refMeta.per_page,
            total: refMeta.total,
            showSizeChanger: false,
            showTotal: (t) => `${t} referrals`,
            onChange: (page) => fetchReferrals(page),
          }}
        />
      </Card>

      {/* ── Redemption History (grouped by date) ── */}
      <Card
        style={{ borderRadius: 12 }}
        title={<Space><WalletOutlined /><span>Redemption History</span></Space>}
        loading={loadingWallet}
      >
        {Object.keys(logGroups).length === 0 ? (
          <Text type="secondary">No redemptions yet.</Text>
        ) : (
          Object.entries(logGroups).map(([date, logs]) => (
            <div key={date} style={{ marginBottom: 20 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase',
                letterSpacing: 1, padding: '4px 0', borderBottom: '1px solid #f0f0f0', marginBottom: 8,
              }}>
                {date}
              </div>
              {logs.map((log) => (
                <div
                  key={log.id}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 12px', background: '#fafafa', borderRadius: 8, marginBottom: 6,
                  }}
                >
                  <div>
                    <Text strong style={{ fontSize: 15, color: '#1a6b2a' }}>{formatCurrency(log.amount)}</Text>
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                      {log.referrals_count} referral{log.referrals_count !== 1 ? 's' : ''} · {log.referrer_percentage}%
                    </Text>
                    {log.paid_at && (
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                        · Paid {formatDate(log.paid_at)}
                      </Text>
                    )}
                  </div>
                  {log.status === 'paid'
                    ? <Tag color="green" icon={<CheckCircleOutlined />}>Paid</Tag>
                    : <Tag color="orange">Pending Payout</Tag>}
                </div>
              ))}
            </div>
          ))
        )}
      </Card>

      {/* ── Confirm Redeem Modal ── */}
      <Modal
        open={confirmOpen}
        title={<Space><WalletOutlined /> Redeem Pending Commissions</Space>}
        onCancel={() => setConfirmOpen(false)}
        footer={null}
        width={440}
      >
        <div style={{ marginTop: 16 }}>
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Card size="small" style={{ background: '#f6ffed', border: 'none', textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#1a6b2a' }}>{eligibleCount}</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>Eligible Referrals</div>
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small" style={{ background: '#e6f4ff', border: 'none', textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#1677ff' }}>{formatCurrency(eligibleAmount)}</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>Est. Commission</div>
              </Card>
            </Col>
          </Row>
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            All eligible commissions will be added to your wallet in one go. Once redeemed, you can request a payout from our team.
          </Paragraph>
          <Divider style={{ margin: '12px 0' }} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              type="primary" loading={redeeming} icon={<CheckCircleOutlined />}
              onClick={handleRedeem}
              style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}
            >
              Confirm Redeem
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Redeem Success Modal ── */}
      <Modal
        open={successOpen}
        title={<Space><CheckCircleOutlined style={{ color: '#52c41a' }} /> Redeemed Successfully!</Space>}
        onCancel={() => setSuccessOpen(false)}
        footer={null}
        width={540}
      >
        {redeemResult && (
          <div style={{ marginTop: 12 }}>
            <Table
              rowKey="subscription_id"
              dataSource={redeemResult.plan_breakdown || []}
              size="small"
              pagination={false}
              columns={[
                { title: 'Plan',       dataIndex: 'plan_name',             render: (v) => <Tag color={PLAN_BADGE[redeemResult?.subscription_id] || 'default'}>{v}</Tag> },
                { title: 'Referrals',  dataIndex: 'referrals_count',       width: 80, render: (v) => <Text style={{ fontWeight: 600 }}>{v}</Text> },
                { title: 'Price',      dataIndex: 'plan_price',            width: 90, render: (v) => formatCurrency(v) },
                { title: '%',          dataIndex: 'effective_percentage',  width: 60, render: (v) => `${v}%` },
                { title: 'Commission', dataIndex: 'commission',            width: 100, render: (v) => <Text style={{ fontWeight: 700, color: '#1a6b2a' }}>{formatCurrency(v)}</Text> },
              ]}
              summary={(rows) => {
                const totalRefs = rows.reduce((s, r) => s + (r.referrals_count || 0), 0);
                const totalComm = rows.reduce((s, r) => s + (r.commission || 0), 0);
                return (
                  <Table.Summary.Row style={{ background: '#f6ffed' }}>
                    <Table.Summary.Cell><Text strong>Total</Text></Table.Summary.Cell>
                    <Table.Summary.Cell><Text strong>{totalRefs}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell />
                    <Table.Summary.Cell />
                    <Table.Summary.Cell><Text strong style={{ color: '#1a6b2a' }}>{formatCurrency(totalComm)}</Text></Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
            <div style={{ textAlign: 'center', marginTop: 16, padding: '12px', background: '#f6ffed', borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: '#9ca3af' }}>New Wallet Balance</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#1a6b2a' }}>
                {formatCurrency(redeemResult.new_wallet_balance)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <Button onClick={() => setSuccessOpen(false)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
